import type { AcaoPar, EscopoExclusao, EscopoQtdValor, EscoposExclusao, ItemPreVisualizacaoCsv, ResultadoImportacaoCsv } from '../types'

/** O que a pessoa escolheu para um par (padrão: manter o existente). */
export interface EscolhaPar { acao: AcaoPar; escopo: EscopoExclusao }
export const ESCOLHA_PADRAO: EscolhaPar = { acao: 'manter', escopo: 'Apenas' }

const r2 = (n: number) => Math.round(n * 100) / 100

/** Parcelado = tem número de parcela e os escopos maiores (compra à vista também tem compraId, por isso não serve de critério). */
export const ehParcelado = (parcelaNumero?: number | null, escopos?: EscoposExclusao | null) =>
  parcelaNumero != null && !!escopos?.estaESeguintes && !!escopos?.todas

export function escopoDe(escopos: EscoposExclusao, escopo: EscopoExclusao): EscopoQtdValor {
  if (escopo === 'EstaESeguintes' && escopos.estaESeguintes) return escopos.estaESeguintes
  if (escopo === 'Todas' && escopos.todas) return escopos.todas
  return escopos.apenas
}

/** O servidor recusa o escopo se alguma parcela dele está em fatura paga ou veio da Pluggy. */
export const escopoBloqueado = (e: EscopoQtdValor) => e.temFaturaPaga || e.temPluggy

export const itemTemPar = (i: ItemPreVisualizacaoCsv) => i.situacao === 'nova' && !!i.candidatoPar

/** "Trocar" só vale se o existente (e a fatura de destino) permitem excluir. Devolve o motivo ou null. */
export function motivoSemTrocar(i: ItemPreVisualizacaoCsv, destinoPago: boolean): string | null {
  const c = i.candidatoPar
  if (!c) return 'Sem lançamento para trocar.'
  if (c.vindoDaPluggy || c.escopos.apenas.temPluggy) return 'O lançamento existente veio do banco (Pluggy): não dá para excluir.'
  if (destinoPago || c.escopos.apenas.temFaturaPaga) return 'A fatura já está paga: reabra a fatura antes de trocar.'
  return null
}

export interface ExcluidoResumo { valorNestaFatura: number; qtdOutras: number; valorOutras: number }

/** Quanto sai desta fatura e quantas parcelas saem de outras faturas ao excluir `valorDoLancamento` com o escopo dado. */
export function efeitoExclusao(valorDoLancamento: number, escopos: EscoposExclusao, escopo: EscopoExclusao): ExcluidoResumo {
  const e = escopoDe(escopos, escopo)
  return { valorNestaFatura: valorDoLancamento, qtdOutras: Math.max(0, e.qtd - 1), valorOutras: r2(Math.max(0, e.valor - valorDoLancamento)) }
}

export interface EntradaResumo {
  /** Total atual da fatura de destino (0 se ainda não existe). */
  totalAtual: number
  /** Linhas que serão criadas (compras e ajustes; crédito tem valor negativo). */
  criadas: ItemPreVisualizacaoCsv[]
  /** Linhas do arquivo consideradas na comparação (as marcadas + as dos pares). */
  linhasDoArquivo: ItemPreVisualizacaoCsv[]
  /** Total que o extrato informa (PDF); sem ele, usa a soma das linhas do arquivo (CSV). */
  totalArquivo?: number
  trocas: { valor: number; escopos: EscoposExclusao; escopo: EscopoExclusao }[]
  mantidas: number
  diferentes: number
  excluidas: { valor: number; escopos?: EscoposExclusao | null; escopo: EscopoExclusao }[]
  movidosFora: number[]
  movidosDentro: number[]
  /** Parte dos "Pagamento recebido" que vai para a fatura anterior (positivo): sai desta fatura e do que se espera dela. */
  pagamentosNaAnterior?: number
}

export interface ResumoImportacao {
  totalAtual: number
  totalProjetado: number
  totalArquivo: number
  diferenca: number
  igual: boolean
  importar: number
  manter: number
  trocar: number
  diferentes: number
  mover: number
  excluir: number
  parcelasOutras: number
}

export function calcularResumo(e: EntradaResumo): ResumoImportacao {
  const soma = (l: number[]) => l.reduce((t, v) => t + v, 0)
  const trocas = e.trocas.map((t) => efeitoExclusao(t.valor, t.escopos, t.escopo))
  const excl = e.excluidas.map((x) => (x.escopos ? efeitoExclusao(x.valor, x.escopos, x.escopo) : { valorNestaFatura: x.valor, qtdOutras: 0, valorOutras: 0 }))
  const projetado = r2(
    e.totalAtual + soma(e.criadas.map((i) => i.valor))
    - soma(trocas.map((t) => t.valorNestaFatura)) - soma(excl.map((x) => x.valorNestaFatura))
    - soma(e.movidosFora) + soma(e.movidosDentro) + (e.pagamentosNaAnterior ?? 0),
  )
  const arquivo = r2((e.totalArquivo ?? soma(e.linhasDoArquivo.map((i) => i.valor))) + (e.pagamentosNaAnterior ?? 0))
  const diferenca = r2(projetado - arquivo)
  const comPar = e.trocas.length + e.diferentes
  return {
    totalAtual: e.totalAtual, totalProjetado: projetado, totalArquivo: arquivo, diferenca, igual: diferenca === 0,
    importar: Math.max(0, e.criadas.length - comPar), manter: e.mantidas, trocar: e.trocas.length, diferentes: e.diferentes,
    mover: e.movidosFora.length + e.movidosDentro.length, excluir: e.excluidas.length,
    parcelasOutras: soma(trocas.map((t) => t.qtdOutras)) + soma(excl.map((x) => x.qtdOutras)),
  }
}

/** Texto do toast final: só o que for maior que zero. */
export function textoResultado(r: ResultadoImportacaoCsv): string {
  const partes: [number | undefined, string][] = [
    [r.criadas, 'criadas'], [r.marcadas, 'já existentes marcadas'], [r.mantidas, 'mantidas'], [r.trocadas, 'trocadas'],
    [r.diferentes, 'importadas como diferentes'], [r.ajustes, 'ajustes'], [r.excluidas, 'excluídas'], [r.movidas, 'movidas'],
    [r.saldosTransferidos, 'saldo transferido'], [r.pagamentosNaAnterior, 'pagamento(s) lançado(s) na fatura anterior'],
    [r.pagamentosJaImportados, 'pagamento(s) já importado(s), ignorado(s)'],
  ]
  const itens = partes.filter(([n]) => (n ?? 0) > 0).map(([n, t]) => `${n} ${t}`)
  return itens.length ? `Importação concluída: ${itens.join(', ')}.` : 'Importação concluída: nada mudou.'
}
